/**
 * The No Relief state every view reads from one place: the pop-up's list, the NR buttons on both grids and
 * the "(no relief)" in an officer's schedule text all come from the same groups (useNoRelief), and every tap
 * goes through the same ack/undo calls — so a tick in one shows in all of them at once.
 */
import { createContext, useContext, type ReactNode } from 'react';
import type { NoReliefMark } from '@/utils/noRelief';

export interface NoReliefView {
  /** The NR buttons to draw. Empty while a past version is being previewed. */
  marks: NoReliefMark[];
  /** `${counter}:${slot}` keys where a counter goes unmanned now (see noReliefEnds). */
  ends: Set<string>;
  canEdit: boolean;
  /** Do what tapping `mark` means (markAction). */
  toggle: (mark: NoReliefMark) => Promise<void>;
}

const NoReliefContext = createContext<NoReliefView>({
  marks: [], ends: new Set(), canEdit: false, toggle: async () => {},
});

export function NoReliefProvider({ value, children }: { value: NoReliefView; children: ReactNode }) {
  return <NoReliefContext.Provider value={value}>{children}</NoReliefContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const useNoReliefView = () => useContext(NoReliefContext);
